import type { CategoryType } from "@/components/resources/data/posts.types"

export interface ResourceCategory {
  /** Short name for navigation and tags */
  name: string
  /** Singular name shown on the post page */
  categoryName: string
  /** The /resources/{slug} archive page. null: the posts list only in the All feed. */
  archive: {
    /** Page heading */
    title: string
    lead: string
    /** Falls back to `lead` */
    metaDescription?: string
  } | null
}

// Keyed by slug: posts live in content/resources/{slug}/.
export const RESOURCE_CATEGORIES = {
  articles: {
    name: "News",
    categoryName: "News",
    archive: {
      title: "News",
      lead: "News, articles and announcements from the Token Terminal team",
    },
  },
  "crypto-research": {
    name: "Research",
    categoryName: "Research",
    archive: null,
  },
  interview: {
    name: "Interviews",
    categoryName: "Interview",
    archive: {
      title: "Project interviews",
      lead: "Interviews with core contributors in crypto to gain insights into their day-to-day operations",
    },
  },
  "weekly-fundamentals": {
    name: "Newsletters",
    categoryName: "Newsletter",
    archive: null,
  },
  community: {
    name: "Community",
    categoryName: "Community",
    archive: null,
  },
  engineering: {
    name: "Engineering",
    categoryName: "Engineering",
    archive: {
      title: "Engineering",
      lead: "Technical posts from the Token Terminal engineering team",
      metaDescription:
        "Technical posts from the Token Terminal engineering team — architecture decisions, data pipeline design, and lessons from building institutional-grade onchain analytics.",
    },
  },
} as const satisfies Partial<Record<CategoryType, ResourceCategory>>

export type ResourceCategorySlug = keyof typeof RESOURCE_CATEGORIES

/** A category with an archive page. next.config.js redirects the others to /resources. */
export type ArchiveCategorySlug = {
  [K in ResourceCategorySlug]: (typeof RESOURCE_CATEGORIES)[K]["archive"] extends null
    ? never
    : K
}[ResourceCategorySlug]

export const isResourceCategory = (
  slug: string
): slug is ResourceCategorySlug => Object.hasOwn(RESOURCE_CATEGORIES, slug)

export const isArchiveCategory = (slug: string): slug is ArchiveCategorySlug =>
  isResourceCategory(slug) && RESOURCE_CATEGORIES[slug].archive !== null

export const ARCHIVE_CATEGORIES =
  Object.keys(RESOURCE_CATEGORIES).filter(isArchiveCategory)
